import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  Grid2 as Grid,
} from "@mui/material";
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { ChatContentDto, UpdateChatContentDto } from "@/dto/ChatContentDto";
import { Yn } from "@/constants/code";

interface EditFormProps {
  contentInfo: ChatContentDto;
  onEdit: (data: UpdateChatContentDto) => Promise<void>;
  onDelete?: (contentId: number) => Promise<void>;
  onCancel?: () => void;
}

/**
 * QNA 컨텐츠 수정 폼 컴포넌트
 *
 * @description QNA 컨텐츠 정보를 수정할 수 있는 폼 컴포넌트
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
export const EditForm: React.FC<EditFormProps> = ({
  contentInfo,
  onEdit,
  onDelete,
  onCancel,
}) => {
  const [formData, setFormData] = useState<UpdateChatContentDto>({
    category: contentInfo.category,
    content: contentInfo.content,
    keywords: contentInfo.keywords || "",
    actionType: contentInfo.actionType || "",
    actionUrl: contentInfo.actionUrl || "",
    actionText: contentInfo.actionText || "",
    useYn: contentInfo.useYn,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // contentInfo가 변경될 때 폼 데이터 업데이트
  useEffect(() => {
    setFormData({
      category: contentInfo.category,
      content: contentInfo.content,
      keywords: contentInfo.keywords || "",
      actionType: contentInfo.actionType || "",
      actionUrl: contentInfo.actionUrl || "",
      actionText: contentInfo.actionText || "",
      useYn: contentInfo.useYn,
    });
  }, [contentInfo]);

  const handleInputChange =
    (field: keyof UpdateChatContentDto) =>
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
    ) => {
      const value = event.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // 에러 메시지 초기화
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }
    };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.category?.trim()) {
      newErrors.category = "카테고리는 필수입니다.";
    }

    if (!formData.content?.trim()) {
      newErrors.content = "내용은 필수입니다.";
    }

    if (formData.actionUrl && !isValidUrl(formData.actionUrl)) {
      newErrors.actionUrl = "올바른 URL 형식이 아닙니다.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onEdit(formData);
    } catch (error) {
      console.error("수정 실패:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // 원본 데이터로 복원
    setFormData({
      category: contentInfo.category,
      content: contentInfo.content,
      keywords: contentInfo.keywords || "",
      actionType: contentInfo.actionType || "",
      actionUrl: contentInfo.actionUrl || "",
      actionText: contentInfo.actionText || "",
      useYn: contentInfo.useYn,
    });
    setErrors({});

    if (onCancel) {
      onCancel();
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    const confirmed = window.confirm("정말로 이 컨텐츠를 삭제하시겠습니까?");
    if (!confirmed) return;

    setIsSubmitting(true);
    try {
      await onDelete(contentInfo.contentId);
    } catch (error) {
      console.error("삭제 실패:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">컨텐츠 정보 수정</Typography>

        {/* 삭제 버튼 */}
        {onDelete && (
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={isSubmitting}
            sx={{
              backgroundColor: "#d32f2f",
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
            }}
          >
            삭제
          </Button>
        )}
      </Box>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* 기본 정보 */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 1 }}>
              기본 정보
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="카테고리"
              value={formData.category}
              onChange={handleInputChange("category")}
              error={!!errors.category}
              helperText={errors.category}
              required
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>사용 여부</InputLabel>
              <Select
                value={formData.useYn}
                onChange={handleInputChange("useYn")}
                label="사용 여부"
              >
                <MenuItem value={Yn.Y}>사용</MenuItem>
                <MenuItem value={Yn.N}>미사용</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="내용"
              value={formData.content}
              onChange={handleInputChange("content")}
              error={!!errors.content}
              helperText={errors.content}
              multiline
              rows={6}
              required
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="키워드"
              value={formData.keywords}
              onChange={handleInputChange("keywords")}
              helperText="쉼표(,)로 구분하여 입력하세요"
              placeholder="예: 태권도, 수업, 등록"
            />
          </Grid>

          {/* 액션 정보 */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 1 }}>
              액션 정보 (선택사항)
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="액션 타입"
              value={formData.actionType}
              onChange={handleInputChange("actionType")}
              placeholder="예: TRIAL, CONTACT, DEMO"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="액션 텍스트"
              value={formData.actionText}
              onChange={handleInputChange("actionText")}
              placeholder="예: 무료 체험 신청하기"
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="액션 URL"
              value={formData.actionUrl}
              onChange={handleInputChange("actionUrl")}
              error={!!errors.actionUrl}
              helperText={
                errors.actionUrl || "완전한 URL을 입력하세요 (https://...)"
              }
              placeholder="https://example.com"
            />
          </Grid>

          {/* 버튼 영역 */}
          <Grid size={{ xs: 12 }}>
            <Box
              display="flex"
              gap={2}
              justifyContent="flex-end"
              sx={{ mt: 2 }}
            >
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                취소
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={isSubmitting}
              >
                {isSubmitting ? "저장 중..." : "저장"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};
